import { dateDisplayFormat, getLocalDate } from '@alexis/helpers/date';
import { genzoTrackActionEvent } from '@alexis/helpers/genzo';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import { uniqBy } from 'lodash-es';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { clsx } from '@wego/alexis/helpers/clsx';
import { translateNumber, translateText } from '@wego/alexis/helpers/translation';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { getImageTransformationsUrl } from '@helpers/imageTransformations';
import {
  removePrefixZeroFromTimeString,
  convertDurationInMinutesToHoursAndMinutes,
} from '@helpers/utils';

import ArrowRoundedIcon from '@icons/arrow_rounded_new.svg';
import MultiAirlineIcon from '@icons/multi-flight.svg';

import {
  getTranslationsState,
  getLocaleState,
  getUserState,
  getPageViewIdState,
} from '@redux/selectors';

import { User } from '@wegoTypes/user';

import styles from './FlightPerLegSearchStepper.module.scss';

type FlightPerLegSearchStepperProps = {
  wegoAnalyticsClientId: string | undefined;
  wegoAnalyticsClientSessionId: string | undefined;
};

const FlightPerLegSearchStepper: React.FC<FlightPerLegSearchStepperProps> = ({
  wegoAnalyticsClientId,
  wegoAnalyticsClientSessionId,
}) => {
  const translations = useSelector(getTranslationsState);
  const locale = useSelector(getLocaleState);
  const user: User | null = useSelector(getUserState);
  const pageViewIdState = useSelector(getPageViewIdState);

  const { tripViewModels, search } = useFlightSearchResults();

  const { currentlySelectedLegIdPLS, handlePLSLegClear } = useFlightPLS();

  const selectedPLSTripViewModel = useMemo(() => {
    if (currentlySelectedLegIdPLS) {
      return tripViewModels.find((trip) => trip.legs[0].leg.id === currentlySelectedLegIdPLS);
    }
  }, [currentlySelectedLegIdPLS, tripViewModels]);

  const selectedDepartLegPrimaryInfo = useMemo(() => {
    if (selectedPLSTripViewModel) {
      const departureAirportCode = selectedPLSTripViewModel.legs[0].departureAirport.code;
      const departureTime = selectedPLSTripViewModel.legs[0].leg.departureTime;
      const arrivalAirportCode = selectedPLSTripViewModel.legs[0].arrivalAirport.code;
      const arrivalTime = selectedPLSTripViewModel.legs[0].leg.arrivalTime;

      return (
        <>
          <span className={styles.airportCode}>{departureAirportCode}</span>
          <span className={styles.time}>{departureTime}</span>
          <ArrowRoundedIcon className={styles.arrowIcon} />
          <span className={styles.airportCode}>{arrivalAirportCode}</span>
          <span className={styles.time}>{arrivalTime}</span>
        </>
      );
    }
  }, [selectedPLSTripViewModel]);

  const departDateText = useMemo(() => {
    if (search?.legs[0].outboundDate) {
      return dateDisplayFormat(
        getLocalDate(search.legs[0].outboundDate),
        translations.short_months as Array<string>,
        translations.short_weekdays as Array<string>,
        locale === 'fa',
        5,
      );
    }

    return '';
  }, [locale, translations.short_months, translations.short_weekdays, search]);

  const returnDateText = useMemo(() => {
    if (search?.legs[1].outboundDate) {
      return dateDisplayFormat(
        getLocalDate(search.legs[1].outboundDate),
        translations.short_months as Array<string>,
        translations.short_weekdays as Array<string>,
        locale === 'fa',
        5,
      );
    }

    return '';
  }, [locale, translations.short_months, translations.short_weekdays, search]);

  const handleChangeToDepartLegSelection: React.MouseEventHandler<HTMLDivElement> = useCallback(
    (e) => {
      e.stopPropagation();
      handlePLSLegClear();

      if (wegoAnalyticsClientId && wegoAnalyticsClientSessionId) {
        const eventData = {
          category: 'per_leg',
          object: 'change_depart_flight',
          action: 'tap',
          id: pageViewIdState.home,
          value: '',
        };
        genzoTrackActionEvent(
          API_BASE_URL,
          wegoAnalyticsClientId,
          wegoAnalyticsClientSessionId,
          user?.userHash,
          eventData,
        );
      }
    },
    [
      handlePLSLegClear,
      pageViewIdState.home,
      user?.userHash,
      wegoAnalyticsClientId,
      wegoAnalyticsClientSessionId,
    ],
  );

  const stopSection = useMemo(() => {
    if (selectedPLSTripViewModel) {
      const legViewModel = selectedPLSTripViewModel?.legs[0];
      if (legViewModel.segmentViewModels.length === 0) {
        return translations.direct;
      }

      if (legViewModel.segmentViewModels.length === 1) {
        const segmentViewModel = legViewModel.segmentViewModels[0];
        const stopOverTimeText = convertDurationInMinutesToHoursAndMinutes(
          segmentViewModel.segment.stopoverDurationMinutes,
        );

        return translateText(
          translations.duration_in_airport,
          locale,
          stopOverTimeText,
          segmentViewModel.segment.arrivalAirportCode,
        );
      }

      if (
        legViewModel.segmentViewModels.length === 2 ||
        legViewModel.segmentViewModels.length === 3
      )
        return translateText(
          translations['n_stops_in_d'],
          locale,
          legViewModel.segmentViewModels.length,
          legViewModel.segmentViewModels
            .map((segment) => segment.segment.arrivalAirportCode)
            .join(', '),
        );

      return `${translateNumber(legViewModel.segmentViewModels.length, locale === 'fa')} ${
        translations.stops
      }`;
    }
  }, [selectedPLSTripViewModel, locale, translations]);

  const legViewAirlines = useMemo(() => {
    if (selectedPLSTripViewModel) {
      return uniqBy(selectedPLSTripViewModel.legs[0].airlines, 'name');
    }
    return [];
  }, [selectedPLSTripViewModel]);

  return (
    <div className={styles.legSelectBar}>
      <div className={styles.stepperArrow} />

      <div className={clsx(styles.leg, styles.depart, !selectedPLSTripViewModel && styles.active)}>
        <div className={clsx(styles.number, selectedPLSTripViewModel && styles.selected)}>1</div>
        {selectedPLSTripViewModel ? (
          <div className={styles.selectedDepartLegInfoContainer}>
            <div className={styles.selectedLegInfo}>
              <div className={styles.images}>
                {legViewAirlines
                  .slice(0, legViewAirlines.length > 3 ? 2 : 3)
                  .map((airline, index) => (
                    <img
                      key={airline.code}
                      className={index === 0 ? styles.mainImage : styles.subImage}
                      src={getImageTransformationsUrl(
                        `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${airline?.code}.png`,
                      )}
                    />
                  ))}

                {legViewAirlines.length > 3 ? (
                  <MultiAirlineIcon className={styles.subImage} />
                ) : null}
              </div>

              <div className={styles.textContainer}>
                <div className={styles.primaryDetails}>
                  {legViewAirlines.length === 1 ? (
                    <img
                      className={styles.airlineImage}
                      src={getImageTransformationsUrl(
                        `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/airlines_square/${legViewAirlines[0]?.code}.png`,
                      )}
                    />
                  ) : (
                    <MultiAirlineIcon className={styles.airlineImage} />
                  )}
                  {selectedDepartLegPrimaryInfo}
                </div>

                <div className={styles.secondaryDetails}>
                  {`${departDateText} · ${removePrefixZeroFromTimeString(
                    selectedPLSTripViewModel.legs[0].leg.duration,
                  )} · `}

                  <span
                    className={clsx(
                      styles.stops,
                      selectedPLSTripViewModel.legs[0].segmentViewModels.length >= 1 &&
                        styles.nonDirect,
                    )}
                  >
                    {stopSection}
                  </span>
                </div>
              </div>

              <div className={styles.plsChangeButton} onClick={handleChangeToDepartLegSelection}>
                {translations.change}
              </div>
            </div>
          </div>
        ) : (
          <div className={styles.text}>
            <div className={styles.main}>{translations.select_departing_flight}</div>

            <div className={styles.date}>{departDateText}</div>
          </div>
        )}
      </div>

      <div className={clsx(styles.leg, styles.return, !!selectedPLSTripViewModel && styles.active)}>
        <div className={styles.number}>2</div>
        <div className={styles.text}>
          <div className={styles.main}>{translations.select_returning_flight}</div>
          <div className={styles.date}>{returnDateText}</div>
        </div>
      </div>
    </div>
  );
};

export default FlightPerLegSearchStepper;
